import { useEffect, useState } from "react";
import { Plus, Trash2, X, Code2, Clock, AlertTriangle, Gauge } from "lucide-react";
import { api } from "../lib/apiClient";
import { useApp, MONTHS } from "../lib/store";
import { PageHeader, StatusBadge, Empty, StatCard } from "../components/Shared";
import { useEmployees, EmployeePicker } from "../components/Filters";
import { DEV_STATUSES } from "../lib/constants";
import { toast } from "sonner";

const DEV_EMP = ["yograj", "dinesh"];

export default function DevTasks() {
  const { month, year, can, isEmployee, selfEmployeeId, role } = useApp();
  const allowed = role === "super_admin" || (role === "employee" && ["yograj", "dinesh"].includes(selfEmployeeId));
  const employees = useEmployees();
  const [empId, setEmpId] = useState(isEmployee ? selfEmployeeId : "all");
  const [tasks, setTasks] = useState([]);
  const [agg, setAgg] = useState(null);
  const [modal, setModal] = useState(null);

  const devEmps = employees.filter((e) => DEV_EMP.includes(e.id));
  const nameOf = (id) => employees.find((e) => e.id === id)?.name || id;

  const load = () => {
    api.devTasks({ employee_id: empId, month, year }).then((t) => {
      setTasks(t);
      const total = t.length, on = t.filter((x) => ["On Time", "Buffer Absorbed"].includes(x.delay_status)).length;
      const del = t.filter((x) => x.delay_status === "Delayed").length;
      const avg = total ? t.reduce((s, x) => s + (x.auto_score || 0), 0) / total : 0;
      setAgg({ total, on, del, avg: avg.toFixed(2) });
    }).catch(() => {});
  };
  useEffect(() => { load(); }, [empId, month, year]);

  const changeStatus = (t, s) => {
    const body = { status: s };
    if (s === "Completed" && !t.actual_completion_date) body.actual_completion_date = new Date().toISOString();
    api.updateDevTask(t.id, body).then(() => { toast.success(`Status → ${s}`); load(); });
  };

  if (!allowed) return <div className="card-panel p-10 text-center" data-testid="dev-denied"><p className="font-display font-semibold">Restricted</p><p className="text-sm text-muted-c mt-1">Dev tasks are visible only to the developers and Super Admin.</p></div>;

  return (
    <div>
      <PageHeader title="Development Task Tracker" subtitle="Automatic 48-hour buffer applied to every deadline"
        exportModule="dev-tasks" exportParams={{ employee_id: empId, month, year }}>
        {can.assignTasks && <button data-testid="add-dev-task-button" onClick={() => setModal({ employee_id: devEmps[0]?.id, month, year })} className="pill px-3 py-2 text-sm font-semibold brand-bg text-white"><Plus size={15} /> New Task</button>}
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard label="Total Tasks" value={agg?.total ?? "—"} icon={Code2} accent="#2563EB" />
        <StatCard label="On Time / Buffer" value={agg?.on ?? "—"} icon={Clock} accent="#10B981" />
        <StatCard label="Delayed" value={agg?.del ?? "—"} icon={AlertTriangle} accent="#EF4444" />
        <StatCard label="Auto KRA Score" value={agg?.avg ?? "—"} sub="avg / 10" icon={Gauge} accent="#818CF8" />
      </div>

      <div className="card-panel p-4 mb-5 flex flex-wrap gap-3 items-center">
        <EmployeePicker value={empId} onChange={setEmpId} employees={devEmps} includeAll={!isEmployee} disabled={isEmployee} testid="dev-employee-filter" />
        <span className="text-xs text-muted-c ml-auto">{MONTHS.find((m) => m.v === month)?.n} {year}</span>
      </div>

      <div className="card-panel overflow-x-auto" data-testid="dev-table">
        <table className="data-table">
          <thead><tr><th>Employee</th><th>Project</th><th>Task</th><th>Deadline</th><th>Buffer (48h)</th><th>Delivery Date</th><th>Status</th><th>Delay Status</th><th>Score</th>{can.updateTasks && <th></th>}</tr></thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id} data-testid={`dev-row-${t.id}`}>
                <td className="font-medium">{nameOf(t.employee_id)}</td>
                <td className="text-secondary">{t.project}</td>
                <td className="max-w-[180px] truncate">{t.task_title}</td>
                <td className="mono text-xs">{(t.original_deadline || "").slice(0, 10)}</td>
                <td className="mono text-xs text-muted-c">{(t.buffer_deadline || "").slice(0, 10)}</td>
                <td className="mono text-xs">{(t.actual_completion_date || "—").slice(0, 10)}</td>
                <td>
                  {can.updateTasks ? (
                    <select value={t.status} onChange={(e) => changeStatus(t, e.target.value)} className="input-c py-1 text-xs" style={{ width: 130 }} data-testid={`dev-status-dropdown-${t.id}`}>
                      {DEV_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  ) : <StatusBadge status={t.status} />}
                </td>
                <td><StatusBadge status={t.delay_status} /></td>
                <td className="mono font-bold">{t.auto_score}</td>
                {can.updateTasks && <td><button onClick={() => { if (window.confirm("Delete task?")) api.deleteDevTask(t.id).then(() => { toast.success("Deleted"); load(); }); }} className="text-muted-c hover:text-red-500"><Trash2 size={15} /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
        {!tasks.length && <Empty text="No development tasks for this selection" />}
      </div>

      {modal && <DevModal task={modal} emps={devEmps} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function DevModal({ task, emps, onClose, onSaved }) {
  const [f, setF] = useState({
    employee_id: task.employee_id, project: "", task_title: "", task_description: "",
    assigned_date: "", original_deadline: "", actual_completion_date: "", status: "Pending",
    month: task.month, year: task.year,
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    if (!f.task_title) return toast.error("Task title required");
    const body = { ...f };
    ["assigned_date", "original_deadline", "actual_completion_date"].forEach((k) => { body[k] = body[k] ? new Date(body[k]).toISOString() : null; });
    await api.createDevTask(body); toast.success("Task created"); onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose}>
      <div className="card-panel w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="dev-task-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-subtle"><h3 className="font-display font-semibold">New Development Task</h3><button onClick={onClose}><X size={18} className="text-muted-c" /></button></div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <F l="Employee"><select className="input-c" value={f.employee_id} onChange={(e) => set("employee_id", e.target.value)}>{emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select></F>
            <F l="Project"><input className="input-c" value={f.project} onChange={(e) => set("project", e.target.value)} /></F>
          </div>
          <F l="Task Title"><input className="input-c" value={f.task_title} onChange={(e) => set("task_title", e.target.value)} data-testid="dev-title-input" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F l="Assigned Date"><input type="date" className="input-c" value={f.assigned_date} onChange={(e) => set("assigned_date", e.target.value)} /></F>
            <F l="Original Deadline"><input type="date" className="input-c" value={f.original_deadline} onChange={(e) => set("original_deadline", e.target.value)} /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F l="Delivery Date"><input type="date" className="input-c" value={f.actual_completion_date} onChange={(e) => set("actual_completion_date", e.target.value)} /></F>
            <F l="Status"><select className="input-c" value={f.status} onChange={(e) => set("status", e.target.value)}>{DEV_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></F>
          </div>
          <p className="text-xs text-muted-c">A 48-hour buffer is automatically added to the deadline for scoring.</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-subtle"><button onClick={onClose} className="pill px-4 py-2 text-sm border-strong">Cancel</button><button onClick={save} className="pill px-4 py-2 text-sm brand-bg text-white font-semibold" data-testid="save-dev-task-button">Create</button></div>
      </div>
    </div>
  );
}
function F({ l, children }) { return <div><label className="text-xs font-semibold text-muted-c uppercase tracking-wider block mb-1.5">{l}</label>{children}</div>; }
